import React, { useEffect, useState } from "react";
import { BarChart3, Users, Music2, MessageCircle, Headphones, DollarSign, Radio } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

/**
 * AnalyticsPanel — at-a-glance metrics for the host's station.
 *
 * Hits /api/dj/analytics/{slug} and shows: live status, listeners now,
 * total plays (rolling 30 days), top tracks, chat volume, requests
 * total, sessions, and top tippers. The endpoint is public, so this
 * panel works as both a host-internal dashboard and a public widget.
 *
 * `compact` mode: renders a horizontal 4-stat strip suitable for the
 * top broadcast bar (no top-tracks / top-tippers lists). Use this when
 * vertical real-estate is at a premium.
 */
export default function AnalyticsPanel({ slug, days = 30, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!slug) return undefined;
    const load = async () => {
      try {
        const { data } = await api.get(`/dj/analytics/${slug}`, { params: { days } });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally { if (!cancelled) setLoading(false); }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [slug, days]);

  if (loading && !data) {
    return (
      <section data-testid="radio-host-analytics-panel" className={compact ? "text-[10px] text-white/40 italic px-2" : "rounded-xl border border-white/10 bg-black/40 p-3 mb-4"}>
        <p className="text-[11px] text-white/40 italic">Crunching numbers…</p>
      </section>
    );
  }
  if (!data) return null;

  const stats = [
    { label: "Plays", value: data.total_plays || 0, icon: Music2, hint: `last ${data.window_days}d` },
    { label: "Listeners now", value: data.current_listeners || 0, icon: Users, hint: data.live_now ? "ON AIR" : "off-air" },
    { label: "Chat msgs", value: data.chat_messages || 0, icon: MessageCircle, hint: `last ${data.window_days}d` },
    { label: "Requests", value: data.requests_total || 0, icon: Headphones, hint: `last ${data.window_days}d` },
    { label: "Sessions", value: data.sessions_total || 0, icon: Radio, hint: `last ${data.window_days}d` },
  ];

  // Compact horizontal strip — fits inside the broadcast bar.
  if (compact) {
    return (
      <section
        data-testid="radio-host-analytics-panel"
        className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 flex items-center gap-3 overflow-x-auto"
      >
        <div className="flex items-center gap-1 shrink-0">
          <BarChart3 className="w-3.5 h-3.5 text-cyan-300" />
          <span className="text-[9px] uppercase tracking-wider font-black text-white/55">Analytics</span>
          <span className="text-[8px] text-white/35">{data.window_days}d</span>
        </div>
        {stats.map(({ label, value, icon: Icon }) => (
          <div
            key={label}
            data-testid={`analytics-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="flex items-center gap-1 text-[10px] text-white/65 shrink-0"
          >
            <Icon className="w-3 h-3 opacity-70" />
            <span className="text-white font-black tabular-nums text-[12px]">{value}</span>
            <span className="text-white/45">{label}</span>
          </div>
        ))}
      </section>
    );
  }

  return (
    <section data-testid="radio-host-analytics-panel" className="rounded-xl border border-white/10 bg-black/40 p-3 mb-4">
      <header className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <BarChart3 className="w-4 h-4 text-cyan-300 shrink-0" />
          <h3 className="text-[11px] uppercase tracking-[0.25em] font-black text-white/85">Analytics</h3>
          {data.live_now && (
            <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/30 text-emerald-100 border border-emerald-400/60">LIVE</span>
          )}
        </div>
        <span className="text-[10px] text-white/40">{data.window_days}d</span>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
        {stats.map(({ label, value, icon: Icon, hint }) => (
          <div
            key={label}
            data-testid={`analytics-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="bg-white/5 border border-white/10 rounded-lg p-2"
          >
            <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider font-black text-white/55">
              <Icon className="w-3 h-3" /> {label}
            </div>
            <div className="text-lg font-black text-white tabular-nums leading-tight">{value}</div>
            <div className="text-[9px] text-white/40">{hint}</div>
          </div>
        ))}
      </div>

      {data.top_tracks?.length > 0 && (
        <div className="mb-3">
          <h4 className="text-[10px] uppercase tracking-wider font-black text-white/55 mb-1">Top tracks</h4>
          <ol data-testid="analytics-top-tracks" className="space-y-1">
            {data.top_tracks.slice(0, 5).map((t, i) => (
              <li key={`${t.title}-${i}`} className="flex items-center gap-2 text-[12px] bg-white/5 rounded px-2 py-1">
                <span className="text-white/40 font-black tabular-nums w-4">{i + 1}.</span>
                <span className="text-white truncate flex-1">{t.title}{t.artist ? ` · ${t.artist}` : ""}</span>
                <span className="text-tv-accent font-black tabular-nums">{t.plays}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {data.top_tippers?.length > 0 && (
        <div>
          <h4 className="text-[10px] uppercase tracking-wider font-black text-white/55 mb-1">
            <DollarSign className="w-3 h-3 inline-block" /> Top tippers
          </h4>
          <ol data-testid="analytics-top-tippers" className="space-y-1">
            {data.top_tippers.slice(0, 5).map((t, i) => (
              <li key={`${t.user_id}-${i}`} className="flex items-center gap-2 text-[12px] bg-white/5 rounded px-2 py-1">
                <span className="text-white/40 font-black tabular-nums w-4">{i + 1}.</span>
                <span className="text-white truncate flex-1">{t.name || "Anon"}</span>
                <span className="text-emerald-300 font-black tabular-nums">${(t.amount_cents / 100).toFixed(2)}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}
